import React, { useEffect, useMemo, useRef, useState } from "react";
import { DraftAlbum, Genre, RegionSettings, TrackReleaseSet } from "../../../../api/useMusicApi";
import { Config } from "../../../../config"; // @ts-ignore
import FineUploaderTraditional from "fine-uploader-wrappers";
import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons/faEllipsisVertical";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { faChevronUp } from "@fortawesome/free-solid-svg-icons/faChevronUp";
import { TrackItemForm } from "./TrackItemForm";
import { TrackUploadBar } from "./TrackUploadBar";
import { UpdateFields } from "../../../../hooks/use-advanced-state";
import { useDrag, useDrop, XYCoord } from "react-dnd";
import { Identifier } from "dnd-core";
import { StringParam, useQueryParam, withDefault } from "use-query-params";
import { ValidationError } from "../../../../components/ValidationInputWrapper";
import { faFire } from "@fortawesome/free-solid-svg-icons/faFire";
import { toast } from "react-toastify";
import { useTranslation } from "../../../../hooks/useTranslation";
import { PartialNullable } from "../../../../types";
import { truncate } from "../../../../utils";
import { FeatureEnableFlag } from "./FeatureEnableFlag";

type Props = {
  album: PartialNullable<DraftAlbum>;
  isDraft: boolean;
  index: number;
  item: TrackReleaseSet;
  file?: File;
  validationErrors: ValidationError[];
  className?: string;
  isInitExpanded?: boolean;
  onChange: UpdateFields<TrackReleaseSet>;
  onChangeForAll: (field: keyof TrackReleaseSet | string, value: any) => void;
  onMove: (dragIndex: number, hoverIndex: number) => void;
  onDelete: () => void;
  onReloadFile: (trackId: number | string) => void;
  onDragging: (isDragging: boolean) => void;
  genres: Genre[];
};
export const TrackItem: React.FC<Props> = ({
                                             item,
                                             album,
                                             className,
                                             isInitExpanded,
                                             validationErrors,
                                             index,
                                             file,
                                             genres,
                                             isDraft,
                                             onMove,
                                             onReloadFile,
                                             onChange,
                                             onChangeForAll,
                                             onDragging,
                                             onDelete
                                           }) => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(
    isInitExpanded != null ? !isInitExpanded : true
  );
  const [trackId] = useQueryParam("track_id", withDefault(StringParam, null));
  const [uploadedPathBase64, setUploadedPathBase64] = useState<string>();
  const [uploadFileProgress, setUploadFileProgress] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (trackId == item?.id) {
      setIsCollapsed(false);
    }
  }, [trackId]);

  const uploader = useMemo(
    () =>
      new FineUploaderTraditional({
        options: {
          request: {
            endpoint: `${Config.BASE_API_URL}/audio_upload_fine_uploader/upload/`,
            customHeaders: {
              Authorization:
                "Bearer " + localStorage.getItem("CURRENT_USER_TOKEN")
            }
          },
          chunking: {
            enabled: true,
            partSize: 10 * 1000000, // 10MB
            concurrent: {
              enabled: true
            },
            success: {
              endpoint: `${Config.BASE_API_URL}/audio_upload_fine_uploader/upload/?done`
            }
          },
          callbacks: {
            onComplete: (id: number, name: string, responseJSON: any) => {
              setUploadedPathBase64(responseJSON.path_b64);
            },
            onProgress: (
              id: number,
              name: string,
              uploadedBytes: number,
              totalBytes: number
            ) => {
              let fileProgress = parseInt(
                // @ts-ignore
                (uploadedBytes / totalBytes) * 100,
                10
              );
              if (fileProgress < 99) {
                setUploadFileProgress(fileProgress);
              } else {
                setUploadFileProgress(100);
              }
            },
            onError: (id: number, name: string) => {
              toast.error(`${t("error_while_upload_files")}: ${name}`);
              onDelete();
            }
          }
        }
      }),
    []
  );

  const dropRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<any>(null);
  const [{ handlerId }, drop] = useDrop<
    any,
    void,
    { handlerId: Identifier | null }
  >({
    accept: "track",
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId()
      };
    },
    hover(item: any, monitor) {
      if (!dropRef.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = dropRef.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      const clientOffset = monitor.getClientOffset();
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      onMove(dragIndex, hoverIndex);
      item.index = hoverIndex;
    }
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: "track",
    item: () => {
      return { index, item };
    },
    collect: (monitor) => {
      return {
        isDragging: monitor.isDragging()
      };
    }
  });

  useEffect(() => {
    onDragging(isDragging);
  }, [isDragging]);

  useEffect(() => {
    if (file != null) {
      uploader.methods.addFiles([file]);
    }
  }, [file]);

  useEffect(() => {
    if (uploadedPathBase64) {
      onChange({ file_path_b64: uploadedPathBase64 });
    }
  }, [uploadedPathBase64]);

  drag(dragRef);
  preview(drop(dropRef));

  const isNew = item.id.toString().includes("uploaded_");
  return (
    <div
      ref={dropRef}
      id={`track_item_${item?.id}`}
      className={cn("flex flex-col rounded py-3 px-3", className)}
      style={{
        opacity: isDragging ? 0 : 1,
        borderWidth: 2,
        background: "rgba(239,239,239,0.25)",
        borderColor:
          validationErrors.find((e) => e.attr.includes(`${index}.`)) == null
            ? "#dee2e6"
            : "red"
      }}
      data-handler-id={handlerId}
    >
      <div className={"flex justify-between items-center"}>
        <div className={"flex"}>
          <div className={"flex items-center"}>
            <div ref={dragRef} className={"cursor-grabbing"}>
              <FontAwesomeIcon
                size={"lg"}
                icon={faEllipsisVertical}
                className={"mr-3 text-muted"}
              />
            </div>
            <div>
              <FontAwesomeIcon
                icon={isCollapsed ? faChevronDown : faChevronUp}
                className={"mr-3 cursor-pointer text-muted"}
                onClick={() => setIsCollapsed((prev) => !prev)}
                size={"lg"}
              />
            </div>
          </div>

          <div>
            <span className={"text-muted"}>{index + 1}.</span>{" "}
            {item.country_meta?.title?.[RegionSettings.ALL] != null ? (
              <>
                  <span className={"font-bold text-gray-600"}>
                    {truncate(item.country_meta?.title?.[RegionSettings.ALL] ?? "", 80)}
                  </span>

                <span className={"text-muted ml-1"}>
                    {truncate(item.country_meta?.subtitle?.[RegionSettings.ALL] ?? "", 80)}
                  </span>

                <span className={"font-bold text-gray-600"}>
                    {" - "}
                  {item?.country_meta?.artists?.main?.length > 0 && (
                    <>
                      {item?.country_meta?.artists?.main
                        ?.map((a) => a[RegionSettings.ALL])
                        .join(", ")}
                    </>
                  )}
                  </span>

                {item?.focus && (
                  <FontAwesomeIcon
                    icon={faFire}
                    className={"text-orange-500 ml-1"}
                  />
                )}
                {item?.is_explicit && (
                  <span
                    style={{ fontWeight: 900 }}
                    className={"ml-1 text-gray-600"}
                  >
                      E
                    </span>
                )}
              </>
            ) : (
              file?.name ?? item.file_name ?? truncate(item.display_title ?? "", 100)
            )}
          </div>
        </div>


        <div className={cn("flex flex-1 justify-end", { "gap-1 items-center": !isDraft, "gap-[12px]":  isNew})}>
          <div className={"flex gap-1 min-w-[85px] h-[24px] justify-end"}>
            <FeatureEnableFlag
              isActive={item.services?.STREAMING?.clip ?? false}
              imagePath={"/album/clips_logo.png"}
              popupTitle={t("track_features_enable_status.vk_clips")}
            />
            <FeatureEnableFlag
              isActive={item.services?.STREAMING?.story ?? false}
              imagePath={"/album/stories_logo.png"}
              popupTitle={t("track_features_enable_status.vk_stories")}
            />
            <FeatureEnableFlag
              isActive={item.text != null}
              imagePath={"/album/lyrics_logo.png"}
              popupTitle={t("track_features_enable_status.track_lyrics")}
            />
          </div>
          {isDraft && (
            <TrackUploadBar
              file={file}
              fileMeta={item?.file_meta}
              isNew={isNew}
              progress={uploadFileProgress}
              onDelete={onDelete}
              onReloadFile={() => onReloadFile(item.id)}
            />
          )}

          {!isDraft && (
            <div className={"text-sm min-w-[195px] text-muted"}>
              {item.external_id && <div>External ID: {item.external_id}</div>}
              {item.code_isrc && <div>ISRC: {item.code_isrc}</div>}
            </div>
          )}
        </div>
      </div>
      {!isCollapsed && (
        <TrackItemForm
          isDraft={isDraft}
          item={item}
          album={album}
          index={index}
          validationErrors={validationErrors}
          genres={genres}
          onChange={onChange}
          onChangeForAll={onChangeForAll}
        />
      )}
    </div>
  );
};
